import React, { useEffect, useRef, useState } from "react";
import "./medical-pdf-preview.scss";

let pdfRendererPromise;

const loadPdfRenderer = () => {
  if (!pdfRendererPromise) {
    pdfRendererPromise = Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.entry"),
    ]).then(([pdfjs, pdfWorker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker.default;
      return pdfjs;
    });
  }

  return pdfRendererPromise;
};

const clearPages = (container) => {
  while (container?.firstChild) {
    container.removeChild(container.firstChild);
  }
};

export default function MedicalPdfPreview({ source, title }) {
  const pagesContainerRef = useRef(null);
  const [previewState, setPreviewState] = useState({
    isLoading: true,
    error: "",
  });

  useEffect(() => {
    const pagesContainer = pagesContainerRef.current;
    if (!pagesContainer || !source) return undefined;

    let isCancelled = false;
    let loadingTask;
    const renderTasks = [];

    clearPages(pagesContainer);
    setPreviewState({ isLoading: true, error: "" });

    const renderDocument = async () => {
      try {
        const { getDocument } = await loadPdfRenderer();
        if (isCancelled) return;

        loadingTask = getDocument({ url: source });
        const pdfDocument = await loadingTask.promise;

        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          if (isCancelled) return;

          const page = await pdfDocument.getPage(pageNumber);
          const initialViewport = page.getViewport({ scale: 1 });
          const availableWidth = Math.max(pagesContainer.clientWidth - 32, 280);
          const viewport = page.getViewport({
            scale: Math.min(1.5, availableWidth / initialViewport.width),
          });
          const outputScale = Math.min(window.devicePixelRatio || 1, 2);
          const pageContainer = document.createElement("div");
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (!context) throw new Error("No fue posible preparar la vista del PDF.");

          pageContainer.className = "medical-pdf-page";
          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.setAttribute("role", "img");
          canvas.setAttribute(
            "aria-label",
            `${title || "Documento PDF"}, página ${pageNumber} de ${pdfDocument.numPages}`,
          );
          pageContainer.appendChild(canvas);
          pagesContainer.appendChild(pageContainer);

          const renderTask = page.render({
            canvasContext: context,
            viewport,
            transform: outputScale === 1
              ? undefined
              : [outputScale, 0, 0, outputScale, 0, 0],
          });
          renderTasks.push(renderTask);
          await renderTask.promise;
          page.cleanup();
        }

        if (!isCancelled) setPreviewState({ isLoading: false, error: "" });
      } catch {
        if (!isCancelled) {
          setPreviewState({
            isLoading: false,
            error: "No fue posible mostrar el PDF.",
          });
        }
      }
    };

    renderDocument();

    return () => {
      isCancelled = true;
      renderTasks.forEach((renderTask) => renderTask.cancel());
      loadingTask?.destroy();
      clearPages(pagesContainer);
    };
  }, [source, title]);

  return (
    <div className="medical-pdf-preview" aria-busy={previewState.isLoading}>
      {previewState.isLoading ? (
        <div className="medical-pdf-preview-status text-muted">
          Cargando PDF...
        </div>
      ) : null}
      {previewState.error ? (
        <div className="medical-pdf-preview-status text-danger">
          {previewState.error}
        </div>
      ) : null}
      <div ref={pagesContainerRef} className="medical-pdf-pages" />
    </div>
  );
}
